/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Template Grid Selector - Grid layout for cup selection
 * Updated: 2026-01-10
 */

import React from 'react';

interface Template {
  id: string;
  name: string;
  url?: string;
}

interface TemplateCarouselProps {
  templates: Template[];
  selectedTemplate: Template | null;
  onSelect: (template: Template) => void;
  disabled?: boolean;
}

const TemplateCarousel: React.FC<TemplateCarouselProps> = ({
  templates,
  selectedTemplate,
  onSelect,
  disabled = false,
}) => {
  return (
    <div className="bg-white rounded-lg p-2 lg:p-5 shadow-sm border border-[#eee] flex-shrink-0">
      <h3 className="text-[11px] lg:text-base font-bold text-[#333333] mb-2 lg:mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>1. CHỌN MẪU</h3>

      {/* Grid Layout - Horizontal Scrollable - Single Row */}
      <div className="overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch] pb-1.5">
        <div className="flex gap-1.5 lg:gap-3 min-w-min">
        {templates.map((template) => (
          <button
            key={template.id}
            onClick={() => !disabled && onSelect(template)}
            disabled={disabled}
            className={`flex-shrink-0 flex flex-col items-center gap-1 transition-all ${
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            {/* Cup Image - Only border on image */}
            <div className={`w-14 h-18 lg:w-24 lg:h-32 rounded-md overflow-hidden flex items-center justify-center bg-white transition-all border-2 ${
              selectedTemplate?.id === template.id
                ? 'cup-selected shadow-md'
                : 'border-[#eee] hover:border-[#ddd]'
            }`}>
              {template.url ? (
                <img
                  key={template.url}
                  src={template.url}
                  alt={template.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-[8px] lg:text-xs text-[#999] text-center px-0.5" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                  {template.name}
                </span>
              )}
            </div>

            {/* Cup Name */}
            <p className="text-[7px] lg:text-xs font-medium text-[#333333] text-center max-w-14 lg:max-w-24 truncate w-full" style={{ fontFamily: 'Quicksand, sans-serif' }}>
              {template.name}
            </p>
          </button>
        ))}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Quicksand:wght@300;400;500;600;700&display=swap');

        .overflow-x-auto::-webkit-scrollbar {
          height: 6px;
        }
        .overflow-x-auto::-webkit-scrollbar-track {
          background: transparent;
        }
        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Selected cup border color */
        .cup-selected {
          border-color: rgb(6, 182, 212);
          box-shadow: 0 4px 6px -1px rgba(6, 182, 212, 0.2) !important;
        }
      `}</style>
    </div>
  );
};

export default TemplateCarousel;
